import type { PickSampleExt } from "@novorender/api";
import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { measureActions, selectMeasure, selectMeasurePickSettings } from "features/measure";
import type { PointLinePoint } from "features/pointLine";
import { pointLineActions, selectPointLineCurrentIdx, selectPointLines } from "features/pointLine";
import { useAddPointToPointLine } from "features/pointLine/hooks/useAddPointToPointLine";
import { isPointLineClosed } from "features/pointLine/utils";
import { selectCurrentPickerGeneratedParametricData, selectPicker } from "features/render";
import { Picker } from "features/render/types";
import { applyCameraDistanceToMeasureTolerance } from "features/render/utils";
import { undoRedoActions } from "features/undoRedo";
import { hasMouseSupport } from "utils/misc";

import {
    mobileMeasureActions,
    mobileMeasureCursorRef,
    PICK_TARGET_OFFSET,
    selectCursorPosition,
    selectMovingPoint,
    selectPointLineMode,
} from "./mobileMeasureSlice";
import { useMobileMeasureCursor } from "./useMobileMeasureCursor";

const TAP_PROXIMITY_PX = 30;
const TAP_PROXIMITY_SQ = TAP_PROXIMITY_PX * TAP_PROXIMITY_PX;

export function useMobilePointLine() {
    const dispatch = useAppDispatch();
    const {
        state: { view },
    } = useExplorerGlobals();
    const addPointToPointLine = useAddPointToPointLine();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const pointLines = useAppSelector(selectPointLines);
    const currentIndex = useAppSelector(selectPointLineCurrentIdx);
    const measure = useAppSelector(selectMeasure);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const pointLineMode = useAppSelector(selectPointLineMode);
    const movingPoint = useAppSelector(selectMovingPoint);

    const picker = useAppSelector(selectPicker);
    const isMobile = !hasMouseSupport();

    const pointLinesRef = useRef(pointLines);
    pointLinesRef.current = pointLines;
    const currentIndexRef = useRef(currentIndex);
    currentIndexRef.current = currentIndex;
    const modeRef = useRef(pointLineMode);
    modeRef.current = pointLineMode;
    const movingPointRef = useRef(movingPoint);
    movingPointRef.current = movingPoint;
    const cursorPos = useAppSelector(selectCursorPosition);
    const cursorPosRef = useRef(cursorPos);
    cursorPosRef.current = cursorPos;

    const wasPointLinePickerRef = useRef(false);
    useEffect(() => {
        const isPointLinePicker = picker === Picker.PointLine;
        if (isMobile && isPointLinePicker && !wasPointLinePickerRef.current) {
            dispatch(pointLineActions.startDetachedPointLine());
        }
        wasPointLinePickerRef.current = isPointLinePicker;
    }, [picker, isMobile, dispatch]);

    const onDragUpdate = useCallback(
        async (result: PickSampleExt | null) => {
            if (!view || !result || result.objectId === -1) {
                dispatch(mobileMeasureActions.setLoopCloseCandidate(null));
                dispatch(mobileMeasureActions.setCursorVisual("noHit"));
                dispatch(measureActions.selectHoverObj(undefined));
                return;
            }

            const mp = movingPointRef.current ?? mobileMeasureCursorRef.movingPoint;
            if (mp) {
                dispatch(mobileMeasureActions.setLoopCloseCandidate(null));
                const existingPoint = pointLinesRef.current[mp.lineIndex]?.points[mp.pointIndex];
                dispatch(
                    pointLineActions.movePoint(
                        {
                            point: {
                                id: existingPoint?.id ?? 0,
                                position: result.position,
                                snap: { type: "vertex", connection: result.position },
                                objectId: result.objectId,
                            },
                            lineIdx: mp.lineIndex,
                            pointIdx: mp.pointIndex,
                        },
                        view,
                    ),
                );
                dispatch(mobileMeasureActions.setCursorVisual("surfaceSnap"));
                return;
            }

            const tolerance = applyCameraDistanceToMeasureTolerance(
                result.position,
                view.renderState.camera,
                measurePickSettings,
            );
            const entity = await view.measure?.core.pickMeasureEntity(
                result.objectId,
                result.position,
                tolerance,
                allowGeneratedParametric.enabled,
            );

            if (entity?.entity && entity.connectionPoint) {
                dispatch(mobileMeasureActions.setCursorVisual("transparentCircle"));
                dispatch(
                    measureActions.selectHoverObj({
                        ...entity.entity,
                        connectionPoint: entity.connectionPoint,
                    }),
                );
            } else {
                dispatch(measureActions.selectHoverObj(undefined));
                if (modeRef.current === "transparent") {
                    dispatch(mobileMeasureActions.setCursorVisual("transparentCircle"));
                } else {
                    dispatch(mobileMeasureActions.setCursorVisual("surfaceSnap"));
                }
            }

            const mode = modeRef.current;
            const lines = pointLinesRef.current;
            const curIdx = currentIndexRef.current;
            const currentLine = lines[curIdx];
            const cursorPosition = cursorPosRef.current;
            let isLoopCloseCandidate = false;

            if (
                mode === "transparent" &&
                currentLine &&
                currentLine.points.length > 2 &&
                !isPointLineClosed(currentLine.points) &&
                cursorPosition
            ) {
                const firstPoint = currentLine.points[0];
                const firstPointScreenPos = view.measure?.draw.toMarkerPoints([firstPoint.position])?.[0];
                if (firstPointScreenPos) {
                    const nextPointTarget = {
                        x: cursorPosition.x + PICK_TARGET_OFFSET.x,
                        y: cursorPosition.y + PICK_TARGET_OFFSET.y,
                    };
                    const dx = nextPointTarget.x - firstPointScreenPos[0];
                    const dy = nextPointTarget.y - firstPointScreenPos[1];
                    isLoopCloseCandidate = dx * dx + dy * dy < TAP_PROXIMITY_SQ;
                }
            }

            dispatch(
                mobileMeasureActions.setLoopCloseCandidate(
                    isLoopCloseCandidate ? { lineIndex: curIdx, pointIndex: 0 } : null,
                ),
            );
            if (isLoopCloseCandidate) {
                dispatch(mobileMeasureActions.setCursorVisual("pointSnap"));
            }
        },
        [view, dispatch, measurePickSettings, allowGeneratedParametric],
    );

    const onPlace = useCallback(
        async (result: PickSampleExt, canvasPos: { x: number; y: number }) => {
            if (!view) {
                return;
            }

            dispatch(mobileMeasureActions.setLoopCloseCandidate(null));
            dispatch(measureActions.selectHoverObj(undefined));

            const mp = movingPointRef.current ?? mobileMeasureCursorRef.movingPoint;
            if (mp) {
                const originalPoint = mobileMeasureCursorRef.movingPointOriginal;
                await addPointToPointLine({
                    position: result.position,
                    position2d: [canvasPos.x, canvasPos.y],
                    objectId: result.objectId,
                    move: mp,
                });
                if (originalPoint) {
                    const savedOriginal = { ...originalPoint, position: [...originalPoint.position] } as PointLinePoint;
                    dispatch(
                        undoRedoActions.push({
                            description: "Move point",
                            undoCallback: () => {
                                dispatch(
                                    pointLineActions.movePoint(
                                        { point: savedOriginal, lineIdx: mp.lineIndex, pointIdx: mp.pointIndex },
                                        view,
                                    ),
                                );
                            },
                            redoCallback: () => {
                                dispatch(
                                    pointLineActions.movePoint(
                                        {
                                            point: {
                                                ...savedOriginal,
                                                position: result.position,
                                                objectId: result.objectId,
                                            },
                                            lineIdx: mp.lineIndex,
                                            pointIdx: mp.pointIndex,
                                        },
                                        view,
                                    ),
                                );
                            },
                        }),
                    );
                }
                mobileMeasureCursorRef.movingPointOriginal = null;
                mobileMeasureCursorRef.movingPoint = null;
                dispatch(mobileMeasureActions.clearMovingPoint());
                dispatch(pointLineActions.startDetachedPointLine());
                dispatch(mobileMeasureActions.setPointLineMode("arrow"));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                return;
            }

            const mode = modeRef.current;
            const lines = pointLinesRef.current;
            const curIdx = currentIndexRef.current;
            const currentLine = lines[curIdx];

            // In transparent mode, check proximity to existing points for connecting/closing
            if (
                mode === "transparent" &&
                currentLine &&
                currentLine.points.length > 2 &&
                !isPointLineClosed(currentLine.points)
            ) {
                const firstPoint = currentLine.points[0];
                const firstPointScreenPos = view.measure?.draw.toMarkerPoints([firstPoint.position])?.[0];
                if (firstPointScreenPos) {
                    const dx = canvasPos.x - firstPointScreenPos[0];
                    const dy = canvasPos.y - firstPointScreenPos[1];
                    if (dx * dx + dy * dy < TAP_PROXIMITY_SQ) {
                        dispatch(pointLineActions.setPointLineSnapToPoint(0));
                        await addPointToPointLine({
                            position: firstPoint.position,
                            position2d: [firstPointScreenPos[0], firstPointScreenPos[1]],
                            objectId: firstPoint.objectId ?? -1,
                        });
                        // addPointToPointLine with snap calls newPointLine() which moves
                        // currentIndex to a new empty line. Set it back to the closed loop
                        // so the trashbin targets the correct measurement.
                        dispatch(pointLineActions.setCurrent(curIdx));
                        dispatch(mobileMeasureActions.setPointLineMode("selected"));
                        dispatch(mobileMeasureActions.setDpadStyle("normal"));
                        dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                        dispatch(mobileMeasureActions.setActivelyDrawing(false));
                        return;
                    }
                }
            }

            // Normal point creation
            let measureEntity = measure.hover;
            if (!measureEntity && result.objectId !== -1) {
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measurePickSettings,
                );
                const entity = await view.measure?.core.pickMeasureEntity(
                    result.objectId,
                    result.position,
                    tolerance,
                    allowGeneratedParametric.enabled,
                );
                measureEntity = entity?.entity;
            }

            if (mode === "arrow" || mode === "selected") {
                dispatch(pointLineActions.startDetachedPointLine());
                await addPointToPointLine({
                    position: result.position,
                    position2d: [canvasPos.x, canvasPos.y],
                    objectId: result.objectId,
                    measureEntity,
                });
                dispatch(mobileMeasureActions.setPointLineMode("transparent"));
                dispatch(mobileMeasureActions.setDpadStyle("transparent"));
                dispatch(mobileMeasureActions.setCursorVisual("transparentCircle"));
                dispatch(mobileMeasureActions.setActivelyDrawing(true));
            } else {
                await addPointToPointLine({
                    position: result.position,
                    position2d: [canvasPos.x, canvasPos.y],
                    objectId: result.objectId,
                    measureEntity,
                });
            }
        },
        [view, addPointToPointLine, dispatch, measure.hover, measurePickSettings, allowGeneratedParametric],
    );

    const selectPointAsRoot = useCallback(
        (pointIndex: number, lineIndex: number) => {
            if (!view) {
                return;
            }
            const line = pointLinesRef.current[lineIndex];
            if (!line) {
                return;
            }

            mobileMeasureCursorRef.movingPoint = null;
            mobileMeasureCursorRef.movingPointOriginal = null;
            dispatch(mobileMeasureActions.clearMovingPoint());
            dispatch(pointLineActions.setCurrent(lineIndex));

            const point = line.points[pointIndex];
            const screenPos = view.measure?.draw.toMarkerPoints([point.position]);
            if (screenPos?.[0]) {
                const dpadX = screenPos[0][0] - PICK_TARGET_OFFSET.x;
                const dpadY = screenPos[0][1] - PICK_TARGET_OFFSET.y;
                dispatch(mobileMeasureActions.setCursorPosition({ x: dpadX, y: dpadY }));
                dispatch(mobileMeasureActions.setIndicatorPosition({ x: screenPos[0][0], y: screenPos[0][1] }));
            }

            dispatchLastPickSample(lastPickSampleActions.set(null));
            dispatch(mobileMeasureActions.setCursorActive(true));
            dispatch(mobileMeasureActions.setIndicatorTransform(null));
            dispatch(mobileMeasureActions.setPointLineMode("transparent"));
            dispatch(mobileMeasureActions.setDpadStyle("transparent"));
            dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
            dispatch(mobileMeasureActions.setActivelyDrawing(false));
        },
        [view, dispatch, dispatchLastPickSample],
    );

    const onDpadTap = useCallback(() => {
        if (modeRef.current === "moving" || mobileMeasureCursorRef.movingPoint) {
            mobileMeasureCursorRef.movingPoint = null;
            mobileMeasureCursorRef.movingPointOriginal = null;
            dispatch(mobileMeasureActions.clearMovingPoint());
            dispatch(mobileMeasureActions.setPointLineMode("arrow"));
            dispatch(mobileMeasureActions.setDpadStyle("normal"));
            dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
            return;
        }
        if (modeRef.current === "transparent") {
            const curIdx = currentIndexRef.current;
            const currentLine = pointLinesRef.current[curIdx];
            if (currentLine && currentLine.points.length >= 2) {
                dispatch(mobileMeasureActions.setPointLineMode("selected"));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                dispatch(mobileMeasureActions.setActivelyDrawing(false));
            } else {
                dispatch(pointLineActions.startDetachedPointLine());
                dispatch(mobileMeasureActions.setPointLineMode("arrow"));
                dispatch(mobileMeasureActions.setDpadStyle("normal"));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
            }
            return;
        }
        if (modeRef.current === "selected") {
            dispatch(pointLineActions.startDetachedPointLine());
            dispatch(mobileMeasureActions.setPointLineMode("arrow"));
            dispatch(mobileMeasureActions.setDpadStyle("normal"));
            dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
            return;
        }
        if (modeRef.current === "arrow" && view) {
            const pos = cursorPosRef.current;
            if (!pos) {
                return;
            }
            const pickPos = {
                x: pos.x + PICK_TARGET_OFFSET.x,
                y: pos.y + PICK_TARGET_OFFSET.y,
            };
            const lines = pointLinesRef.current;
            for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
                const line = lines[lineIdx];
                if (line.points.length === 0) {
                    continue;
                }
                const positions = line.points.map((p) => p.position);
                const screenPositions = view.measure?.draw.toMarkerPoints(positions);
                if (!screenPositions) {
                    continue;
                }
                const isClosed = isPointLineClosed(line.points);
                const checkCount = isClosed ? line.points.length - 1 : line.points.length;
                for (let i = 0; i < checkCount; i++) {
                    const sp = screenPositions[i];
                    if (!sp) {
                        continue;
                    }
                    const dx1 = pos.x - sp[0];
                    const dy1 = pos.y - sp[1];
                    const dx2 = pickPos.x - sp[0];
                    const dy2 = pickPos.y - sp[1];
                    if (dx1 * dx1 + dy1 * dy1 < TAP_PROXIMITY_SQ || dx2 * dx2 + dy2 * dy2 < TAP_PROXIMITY_SQ) {
                        selectPointAsRoot(i, lineIdx);
                        return;
                    }
                }
            }
        }
    }, [dispatch, view, selectPointAsRoot]);

    const onTapElsewhere = useCallback(
        (canvasPos: { x: number; y: number }) => {
            if (!view) {
                return;
            }

            // Check proximity to points across all lines
            const lines = pointLinesRef.current;
            for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
                const line = lines[lineIdx];
                if (line.points.length === 0) {
                    continue;
                }
                const positions = line.points.map((p) => p.position);
                const screenPositions = view.measure?.draw.toMarkerPoints(positions);
                if (!screenPositions) {
                    continue;
                }
                const isClosed = isPointLineClosed(line.points);
                const checkCount = isClosed ? line.points.length - 1 : line.points.length;
                for (let i = 0; i < checkCount; i++) {
                    const sp = screenPositions[i];
                    if (!sp) {
                        continue;
                    }
                    const dx = canvasPos.x - sp[0];
                    const dy = canvasPos.y - sp[1];
                    if (dx * dx + dy * dy < TAP_PROXIMITY_SQ) {
                        selectPointAsRoot(i, lineIdx);
                        return;
                    }
                }
            }
        },
        [view, selectPointAsRoot],
    );

    const onDpadLongPress = useCallback(() => {
        if (!view) {
            return;
        }
        const pos = cursorPosRef.current;
        if (!pos) {
            return;
        }
        const pickPos = {
            x: pos.x + PICK_TARGET_OFFSET.x,
            y: pos.y + PICK_TARGET_OFFSET.y,
        };
        const lines = pointLinesRef.current;
        for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
            const line = lines[lineIdx];
            if (line.points.length === 0) {
                continue;
            }
            const positions = line.points.map((p) => p.position);
            const screenPositions = view.measure?.draw.toMarkerPoints(positions);
            if (!screenPositions) {
                continue;
            }
            const isClosed = isPointLineClosed(line.points);
            const checkCount = isClosed ? line.points.length - 1 : line.points.length;
            for (let i = 0; i < checkCount; i++) {
                const sp = screenPositions[i];
                if (!sp) {
                    continue;
                }
                const dx1 = pos.x - sp[0];
                const dy1 = pos.y - sp[1];
                const dx2 = pickPos.x - sp[0];
                const dy2 = pickPos.y - sp[1];
                if (dx1 * dx1 + dy1 * dy1 < TAP_PROXIMITY_SQ || dx2 * dx2 + dy2 * dy2 < TAP_PROXIMITY_SQ) {
                    const pt = line.points[i];
                    mobileMeasureCursorRef.movingPointOriginal = pt
                        ? {
                              position: [...pt.position] as [number, number, number],
                              snap: pt.snap,
                              objectId: pt.objectId,
                              id: pt.id,
                          }
                        : null;
                    dispatch(pointLineActions.setCurrent(lineIdx));
                    dispatch(mobileMeasureActions.setMovingPoint({ lineIndex: lineIdx, pointIndex: i }));
                    mobileMeasureCursorRef.movingPoint = { lineIndex: lineIdx, pointIndex: i };
                    const dpadX = sp[0] - PICK_TARGET_OFFSET.x;
                    const dpadY = sp[1] - PICK_TARGET_OFFSET.y;
                    dispatch(mobileMeasureActions.setCursorPosition({ x: dpadX, y: dpadY }));
                    dispatch(mobileMeasureActions.setIndicatorPosition({ x: sp[0], y: sp[1] }));
                    dispatch(mobileMeasureActions.setDpadStyle("normal"));
                    dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                    return;
                }
            }
        }
    }, [view, dispatch]);

    /** A pinch cancels an armed move; otherwise the next release would apply it. */
    const onAbort = useCallback(() => {
        if (modeRef.current !== "moving" && !mobileMeasureCursorRef.movingPoint) {
            return;
        }
        mobileMeasureCursorRef.movingPoint = null;
        mobileMeasureCursorRef.movingPointOriginal = null;
        dispatch(mobileMeasureActions.clearMovingPoint());
        dispatch(mobileMeasureActions.setPointLineMode("arrow"));
        dispatch(mobileMeasureActions.setDpadStyle("normal"));
        dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
    }, [dispatch]);

    useMobileMeasureCursor({
        picker: Picker.PointLine,
        onPlace,
        onDragUpdate,
        onTapElsewhere,
        onDpadTap,
        onDpadLongPress,
        onAbort,
        enabled: !hasMouseSupport(),
        sampleDiscRadius: 4,
        dpadTapCancelsPlace: true,
        persistent: true,
    });
}
